import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./Mainpage.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTachometerAlt,
  faEdit,
  faTruck,
  faClipboardList,
  faSignOutAlt,
  faUserPlus,
  faUserTie,
  faBed,
  faBars,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

const MainPage = ({ userRole: initialUserRole }) => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState(initialUserRole);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [availableCards, setAvailableCards] = useState([]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("userrole");
    localStorage.removeItem("driverName");
    localStorage.removeItem("driverId");
    localStorage.removeItem("badge");
    navigate("/");
  }, [navigate]);

  // Verify authentication and role
  const verifyAuth = useCallback(() => {
    const token = localStorage.getItem("token");
    const storedUserRole = localStorage.getItem("userrole");

    if (!token || !storedUserRole) {
      handleLogout();
      return false;
    }

    try {
      // Try to parse the JWT token
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const payload = JSON.parse(window.atob(base64));

      // Check if token matches stored role
      if (payload.userrole !== storedUserRole) {
        handleLogout();
        return false;
      }

      return true;
    } catch (error) {
      console.error("Invalid token format:", error);
      handleLogout();
      return false;
    }
  }, [handleLogout]);

  // Navigation functions
  const goToAddComponents = () => {
    navigate("/add-record");
    setIsMobileMenuOpen(false);
  };

  const goToDashboard = () => {
    navigate("/dashboard");
    setIsMobileMenuOpen(false);
  };

  const goToModifyRecords = () => {
    navigate("/modify-records");
    setIsMobileMenuOpen(false);
  };

  const goToBreakdownTracker = () => {
    navigate("/breakdown-tracker");
    setIsMobileMenuOpen(false);
  };

  const goToDriverChecklist = () => {
    navigate("/driver-checklists/");
    setIsMobileMenuOpen(false);
  };

  const goToAddUser = () => {
    navigate("/add-user");
    setIsMobileMenuOpen(false);
  };

  const goToDriverManagement = () => {
    navigate("/driver-management");
    setIsMobileMenuOpen(false); 
  };

  const goToDriverRestDashboard = () => {
    navigate("/driver-rest-dashboard");
    setIsMobileMenuOpen(false);
  };



  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Build available cards based on user role
  const buildAvailableCards = useCallback(() => {
    const cards = [];

    if (userRole === "aljm") {
      cards.push(
        {
          id: "logout",
          title: "Logout",
          description: "Exit the system",
          icon: faSignOutAlt,
          color: "card-logout",
          onClick: handleLogout,
          enabled: true,
          isLogout: true,
        }
      );
      setAvailableCards(cards);
      return;
    }

    // JM Role - Only Add Record and Driver Management
    if (userRole === "JM") {
      cards.push(
        {
          id: "addRecord",
          title: "Add Record",
          description: "Create new entries",
          icon: faPlus,
          color: "card-add",
          onClick: goToAddComponents,
          enabled: true,
        },
        {
          id: "driverManagement",
          title: "Driver Management",
          description: "Personnel administration",
          icon: faUserTie,
          color: "card-driver-mgmt",
          onClick: goToDriverManagement,
          enabled: true,
        }
      );
    }



    // For non-driver, non-oc, and non-JM users
    if (userRole !== "driver" && userRole !== "oc" && userRole !== "JM") {
      cards.push(
        {
          id: "addRecord",
          title: "Add Record",
          description: "Create new entries",
          icon: faPlus,
          color: "card-add",
          onClick: goToAddComponents,
          enabled: true,
        },
        {
          id: "dashboard",
          title: "Dashboard",
          description: "Performance metrics",
          icon: faTachometerAlt,
          color: "card-dashboard",
          onClick: goToDashboard,
          enabled: true,
        },
        {
          id: "modifyRecords",
          title: "Modify Records",
          description: "Update existing data",
          icon: faEdit,
          color: "card-modify",
          onClick: goToModifyRecords,
          enabled: true,
        },
        {
          id: "breakdownTracker",
          title: "Breakdown Tracker",
          description: "Monitor vehicle issues",
          icon: faTruck,
          color: "card-breakdown",
          onClick: goToBreakdownTracker,
          enabled: true,
        }
      );

      // Admin only - Add Users
      if (userRole === "admin") {
        cards.push({
          id: "addUser",
          title: "Add Users",
          description: "Manage system access",
          icon: faUserPlus,
          color: "card-add-user",
          onClick: goToAddUser,
          enabled: true,
        });
      }

      cards.push(
        {
          id: "driverManagement",
          title: "Driver Management",
          description: "Personnel administration",
          icon: faUserTie,
          color: "card-driver-mgmt",
          onClick: goToDriverManagement,
          enabled: true,
        },
        {
          id: "driverRest",
          title: "Driver Rest Dashboard",
          description: "Compliance monitoring",
          icon: faBed,
          color: "card-rest",
          onClick: goToDriverRestDashboard,
          enabled: true,
        }
      );
    }

    // OC Role - Show all cards but only enable Dashboard
    if (userRole === "oc") {
      cards.push(
        {
          id: "addRecord",
          title: "Add Record",
          description: "Create new entries",
          icon: faPlus,
          color: "card-add",
          onClick: null,
          enabled: false,
        },
        {
          id: "dashboard",
          title: "Dashboard",
          description: "Performance metrics",
          icon: faTachometerAlt,
          color: "card-dashboard",
          onClick: goToDashboard,
          enabled: true,
        },
        {
          id: "modifyRecords",
          title: "Modify Records",
          description: "Update existing data",
          icon: faEdit,
          color: "card-modify",
          onClick: null,
          enabled: false,
        },
        {
          id: "breakdownTracker",
          title: "Breakdown Tracker",
          description: "Monitor vehicle issues",
          icon: faTruck,
          color: "card-breakdown",
          onClick: null,
          enabled: false,
        },
        {
          id: "addUser",
          title: "Add Users",
          description: "Manage system access",
          icon: faUserPlus,
          color: "card-add-user",
          onClick: null,
          enabled: false,
        },
        {
          id: "driverManagement",
          title: "Driver Management",
          description: "Personnel administration",
          icon: faUserTie,
          color: "card-driver-mgmt",
          onClick: null,
          enabled: false,
        },
        {
          id: "driverRest",
          title: "Driver Rest Dashboard",
          description: "Compliance monitoring",
          icon: faBed,
          color: "card-rest",
          onClick: null,
          enabled: false,
        }
      );
    }

    // Driver Checklist - Always shown
    cards.push({
      id: "driverChecklist",
      title: "Driver Checklist",
      description: "Pre-trip inspections",
      icon: faClipboardList,
      color: "card-checklist",
      onClick: goToDriverChecklist,
      enabled: userRole !== "oc",
    });

    // Logout - Always enabled and last
    cards.push({
      id: "logout",
      title: "Logout",
      description: "Exit the system",
      icon: faSignOutAlt,
      color: "card-logout",
      onClick: handleLogout,
      enabled: true,
      isLogout: true,
    });

    setAvailableCards(cards);
  }, [userRole, handleLogout]);

  useEffect(() => {
    // Verify on initial load and page refresh
    if (window.performance && window.performance.navigation.type === 1) {
      if (!verifyAuth()) return;
    }

    const storedUserRole = localStorage.getItem("userrole");
    if (!storedUserRole) {
      handleLogout();
      return;
    }

    setUserRole(storedUserRole);

    // Add event listener for storage changes
    const handleStorageChange = (e) => {
      if (e.key === "userrole" || e.key === "token") {
        if (!verifyAuth()) return;
        setUserRole(localStorage.getItem("userrole"));
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [verifyAuth, handleLogout]);

  useEffect(() => {
    buildAvailableCards();
  }, [buildAvailableCards]);

  // Calculate grid class based on number of cards
  const getGridClass = () => {
    const count = availableCards.length;
    if (count <= 3) return "grid-small";
    if (count <= 6) return "grid-medium";
    return "grid-large";
  };

  return (
    <div className="perfect-dashboard">
      <img
        src="backgroundQ.png"
        alt="OQGN Background"
        className="truck-background-blur"
      />

      <header className="perfect-header">
        <img
          src="OQGN.png"
          alt="OQGN Logo"
          className="corner-logo left-logo"
        />

        <div className="center-titles">
          <h1>AMLS OQGN Dashboard</h1>
        </div>

        <div className="right-logos-container">
          <img src="almadinalog.png" alt="AMLS Logo" className="corner-logo" />
          <img src="bayanat.png" alt="BT Logo" className="corner-logo" />
        </div>

        <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
          <FontAwesomeIcon icon={isMobileMenuOpen ? faTimes : faBars} />
        </button>
      </header>

      <main className="perfect-main-content">
        <div
          className={`dashboard-grid ${getGridClass()} ${
            isMobileMenuOpen ? "mobile-menu-open" : ""
          }`}
        >
          {availableCards.map((card) => (
            <div
              key={card.id}
              className={`card-item ${card.isLogout ? "logout-card" : ""}`}
            >
              <button
                className={`perfect-card ${card.color} ${
                  !card.enabled ? "card-disabled" : ""
                }`}
                onClick={card.enabled ? card.onClick : undefined}
                disabled={!card.enabled}
              >
                <div className="card-icon">
                  <FontAwesomeIcon icon={card.icon} />
                </div>
                <h3>{card.title}</h3>
                <p>{card.description}</p>
                {!card.enabled && (
                  <div className="card-disabled-overlay">
                    <span>Access Restricted</span>
                  </div>
                )}
              </button>
            </div>
          ))}
        </div> 
      </main>

      <footer className="perfect-footer">
        <p>© {new Date().getFullYear()} Bayanat Technology LLC</p>
      </footer>
    </div>
  );
};

export default MainPage;
